// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Channels from '@app/features/channel/state/Channels';
import * as OnboardingCommands from '@app/features/guild/commands/OnboardingCommands';
import Guilds from '@app/features/guild/state/Guilds';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import type {OnboardingStatusResponse} from '@fluxer/schema/src/domains/guild/OnboardingSchemas';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

export const MarkdownText: React.FC<{guildId: string; content: string}> = observer(({guildId, content}) => (
	<div className={markupStyles.markup} data-flx="guild.modals.guild-onboarding-modals.markdown-text">
		<SafeMarkdown
			content={content}
			options={{context: MarkdownContext.STANDARD_WITHOUT_JUMBO, guildId, disableEmojiInfoCard: true}}
			data-flx="guild.modals.guild-onboarding-modals.markdown-text-safe-markdown"
		/>
	</div>
));

export const RulesList: React.FC<{guildId: string; rules: ReadonlyArray<string>}> = observer(({guildId, rules}) => (
	<ol data-flx="guild.modals.guild-onboarding-modals.rules-list" style={{paddingLeft: '1.25rem', margin: '0.75rem 0'}}>
		{rules.map((rule, index) => (
			<li key={`${index}-${rule}`} data-flx="guild.modals.guild-onboarding-modals.rule-item">
				<MarkdownText guildId={guildId} content={rule} data-flx="guild.modals.guild-onboarding-modals.rule-text" />
			</li>
		))}
	</ol>
));

const RulesBody: React.FC<{guildId: string; communityName: string; rules: ReadonlyArray<string>}> = observer(
	({guildId, communityName, rules}) => (
		<div data-flx="guild.modals.guild-onboarding-modals.rules-body">
			<p data-flx="guild.modals.guild-onboarding-modals.rules-intro">
				<Trans>Read and accept the rules to start sending messages in {communityName}.</Trans>
			</p>
			<RulesList guildId={guildId} rules={rules} data-flx="guild.modals.guild-onboarding-modals.rules-body-list" />
		</div>
	),
);

const GuildRulesModal: React.FC<{guildId: string; rules: ReadonlyArray<string>; onAccepted: () => void}> = observer(
	({guildId, rules, onAccepted}) => {
		const {t} = useLingui();
		const communityName = Guilds.getGuild(guildId)?.name ?? '';
		return (
			<ConfirmModal
				title={t`Community rules`}
				description={
					<RulesBody
						guildId={guildId}
						communityName={communityName}
						rules={rules}
						data-flx="guild.guild-onboarding-modals.guild-rules-modal.rules-body"
					/>
				}
				primaryText={t`I Agree`}
				secondaryText={t`Not now`}
				onPrimary={async () => {
					await OnboardingCommands.acceptRules(guildId);
					onAccepted();
				}}
				data-flx="guild.modals.guild-onboarding-modals.rules-modal"
			/>
		);
	},
);

const WelcomeBody: React.FC<{guildId: string; status: OnboardingStatusResponse}> = observer(({guildId, status}) => {
	const channels = status.welcome_channels;
	return (
		<div data-flx="guild.modals.guild-onboarding-modals.welcome-body">
			{status.welcome_description && (
				<MarkdownText
					guildId={guildId}
					content={status.welcome_description}
					data-flx="guild.modals.guild-onboarding-modals.welcome-description"
				/>
			)}
			{channels.length > 0 && (
				<>
					<p data-flx="guild.modals.guild-onboarding-modals.welcome-channels-title">
						<strong data-flx="guild.guild-onboarding-modals.welcome-body.strong">
							<Trans>Good places to start</Trans>
						</strong>
					</p>
					<ul
						data-flx="guild.modals.guild-onboarding-modals.welcome-channels"
						style={{
							listStyle: 'none',
							padding: 0,
							margin: '0.5rem 0',
							display: 'flex',
							flexDirection: 'column',
							gap: '0.5rem',
						}}
					>
						{channels.map((entry) => (
							<li key={entry.channel_id} data-flx="guild.modals.guild-onboarding-modals.welcome-channel">
								<button
									type="button"
									onClick={() => {
										ModalCommands.pop();
										NavigationCommands.selectChannel(guildId, entry.channel_id);
									}}
									style={{all: 'unset', cursor: 'pointer', display: 'block'}}
									data-flx="guild.modals.guild-onboarding-modals.welcome-channel-button"
								>
									<strong data-flx="guild.guild-onboarding-modals.welcome-body.strong--2">
										#{channelName(entry.channel_id)}
									</strong>
									<br data-flx="guild.guild-onboarding-modals.welcome-body.br" />
									<span style={{opacity: 0.8}} data-flx="guild.guild-onboarding-modals.welcome-body.span">
										{entry.description}
									</span>
								</button>
							</li>
						))}
					</ul>
				</>
			)}
		</div>
	);
});

function channelName(channelId: string): string {
	return Channels.getChannel(channelId)?.name ?? channelId;
}

const GuildWelcomeModal: React.FC<{guildId: string; status: OnboardingStatusResponse}> = observer(
	({guildId, status}) => {
		const {t} = useLingui();
		const communityName = Guilds.getGuild(guildId)?.name ?? '';
		return (
			<ConfirmModal
				title={t`Welcome to ${communityName}!`}
				description={
					<WelcomeBody
						guildId={guildId}
						status={status}
						data-flx="guild.guild-onboarding-modals.guild-welcome-modal.welcome-body"
					/>
				}
				primaryText={t`Let's go`}
				secondaryText={false}
				onPrimary={() => undefined}
				data-flx="guild.modals.guild-onboarding-modals.welcome-modal"
			/>
		);
	},
);

function openWelcomeModal(guildId: string, status: OnboardingStatusResponse): void {
	void OnboardingCommands.markWelcomeSeen(guildId);
	ModalCommands.push(
		modal(() => (
			<GuildWelcomeModal
				guildId={guildId}
				status={status}
				data-flx="guild.guild-onboarding-modals.open-welcome-modal.guild-welcome-modal"
			/>
		)),
	);
}

function openRulesModal(guildId: string, status: OnboardingStatusResponse): void {
	ModalCommands.push(
		modal(() => (
			<GuildRulesModal
				guildId={guildId}
				rules={status.rules}
				onAccepted={() => {
					if (status.show_welcome) {
						// The rules modal closes itself after this resolves; open the welcome screen right after.
						setTimeout(() => openWelcomeModal(guildId, status), 0);
					}
				}}
				data-flx="guild.guild-onboarding-modals.open-rules-modal.guild-rules-modal"
			/>
		)),
	);
}

const sessionPrompted = new Set<string>();

// Shows what a member still has to do in a community. Returns true when a modal was opened.
export async function presentGuildOnboarding(guildId: string, options: {force?: boolean} = {}): Promise<boolean> {
	const key = guildId;
	if (!options.force && sessionPrompted.has(key)) return false;
	try {
		const status = await OnboardingCommands.fetchOnboarding(guildId);
		if (status.rules_pending) {
			sessionPrompted.add(key);
			openRulesModal(guildId, status);
			return true;
		}
		if (status.show_welcome) {
			sessionPrompted.add(key);
			openWelcomeModal(guildId, status);
			return true;
		}
	} catch {
		// Onboarding is optional; a failed lookup must never block the community from loading.
	}
	return false;
}
